import { z } from "zod";

const JsonRecordSchema = z.record(z.string(), z.unknown());

export const OfflineAdjustmentOperationSchema = z.enum([
  "VOID_ORDER",
  "REFUND_ORDER",
  "MOVE_TABLE",
  "MERGE_ORDERS",
]);
export type OfflineAdjustmentOperation = z.infer<
  typeof OfflineAdjustmentOperationSchema
>;

export const OfflineAdjustmentActorSchema = z.object({
  userId: z.string().optional(),
  name: z.string().optional(),
  role: z.string().optional(),
});
export type OfflineAdjustmentActor = z.infer<
  typeof OfflineAdjustmentActorSchema
>;

export const POSSelectionSnapshotSchema = z.object({
  branchId: z.string().min(1),
  branchName: z.string().min(1),
  deviceId: z.string().min(1),
  deviceName: z.string().min(1),
  salespersonId: z.string().optional(),
  salespersonName: z.string().optional(),
  receiptPrinterId: z.string().optional(),
  receiptPrinterName: z.string().optional(),
  kitchenPrinterId: z.string().optional(),
  kitchenPrinterName: z.string().optional(),
  tableId: z.string().optional(),
  tableName: z.string().optional(),
  selectedAt: z.string().min(1),
});
export type POSSelectionSnapshot = z.infer<typeof POSSelectionSnapshotSchema>;

export const CartItemModifierSnapshotSchema = z.object({
  modifierId: z.string().min(1),
  modifierName: z.string().min(1),
  optionValue: z.string().min(1),
  optionLabel: z.string().min(1),
  priceDelta: z.number(),
});
export type CartItemModifierSnapshot = z.infer<
  typeof CartItemModifierSnapshotSchema
>;

export const CartItemSnapshotSchema = z.object({
  id: z.string().min(1),
  productId: z.string().min(1),
  productName: z.string().min(1),
  variantId: z.string().min(1),
  variantName: z.string().min(1),
  price: z.number().min(0),
  quantity: z.number().positive(),
  modifiers: z.array(CartItemModifierSnapshotSchema),
  selectedOptions: z.record(z.string(), z.string()),
  note: z.string().optional(),
  totalPrice: z.number().min(0),
});
export type CartItemSnapshot = z.infer<typeof CartItemSnapshotSchema>;

export const DeliveryInputSnapshotSchema = z.object({
  partnerId: z.string().min(1),
  instructions: z.string().optional(),
});
export type DeliveryInputSnapshot = z.infer<typeof DeliveryInputSnapshotSchema>;

export const PaymentInputSnapshotSchema = z.object({
  methodId: z.string().min(1),
  // ອະນຸຍາດ 0 ສຳລັບ order ທີ່ສ່ວນຫຼຸດ/ຄູປອງເຕັມຈຳນວນ (ຍອດສຸດທິ = 0).
  // online checkout ກໍ່ສ້າງ payment amountGross 0 ແບບນີ້ ແລະ completeOrder ຮອງຮັບຢູ່ແລ້ວ.
  amountGross: z.number().nonnegative(),
  referenceCode: z.string().optional(),
  metadata: JsonRecordSchema.optional(),
});
export type PaymentInputSnapshot = z.infer<typeof PaymentInputSnapshotSchema>;

export const CartCouponApplicationSchema = z.object({
  code: z.string().min(1),
  cartIndices: z.array(z.number().int().min(0)).optional(),
});
export type CartCouponApplication = z.infer<typeof CartCouponApplicationSchema>;

export const PaymentCouponApplicationSchema = z.object({
  code: z.string().min(1),
  orderItemIds: z.array(z.string().min(1)).optional(),
  cartIndices: z.array(z.number().int().min(0)).optional(),
});
export type PaymentCouponApplication = z.infer<
  typeof PaymentCouponApplicationSchema
>;

export const LocalOrderCustomerSnapshotSchema = z.object({
  customerId: z.string().optional(),
  localCustomerId: z.string().optional(),
  phone: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  source: z.enum(["server", "cache", "local_draft"]),
  capturedAt: z.string().datetime(),
});
export type LocalOrderCustomerSnapshot = z.infer<
  typeof LocalOrderCustomerSnapshotSchema
>;

export const LocalOrderCouponSnapshotSchema = z.object({
  code: z.string().min(1),
  couponId: z.string().optional(),
  discountType: z.enum(["PERCENT", "AMOUNT"]).optional(),
  discountValue: z.number().optional(),
  discountAmount: z.number(),
  applyScope: z.enum(["order", "cart_indices", "order_items"]),
  cartIndices: z.array(z.number().int().min(0)).optional(),
  orderItemIds: z.array(z.string().min(1)).optional(),
  baseAmount: z.number(),
  validatedAt: z.string().datetime(),
  source: z.enum(["network", "cache"]),
});
export type LocalOrderCouponSnapshot = z.infer<
  typeof LocalOrderCouponSnapshotSchema
>;

export const LocalOrderVoucherSnapshotSchema = z.object({
  code: z.string().min(1),
  voucherId: z.string().optional(),
  value: z.number(),
  appliedAmount: z.number(),
  validatedAt: z.string().datetime(),
  source: z.enum(["network", "cache"]),
});
export type LocalOrderVoucherSnapshot = z.infer<
  typeof LocalOrderVoucherSnapshotSchema
>;

export const LocalOrderRewardSnapshotSchema = z.object({
  rewardId: z.string().min(1),
  rewardName: z.string().min(1),
  pointsCost: z.number(),
  fulfillmentType: z.string().min(1),
  discountAmount: z.number(),
  capturedAt: z.string().datetime(),
  source: z.enum(["network", "cache"]),
  productVariantId: z.string().optional(),
});
export type LocalOrderRewardSnapshot = z.infer<
  typeof LocalOrderRewardSnapshotSchema
>;

export const LocalOrderDiscountBreakdownItemSchema = z.object({
  id: z.string().min(1),
  source: z.enum(["coupon", "voucher", "manual", "loyalty_reward"]),
  code: z.string().optional(),
  label: z.string().min(1),
  amount: z.number(),
  scope: z.enum(["order", "line"]),
  cartIndices: z.array(z.number().int().min(0)).optional(),
  orderItemIds: z.array(z.string().min(1)).optional(),
});
export type LocalOrderDiscountBreakdownItem = z.infer<
  typeof LocalOrderDiscountBreakdownItemSchema
>;

export const LocalOrderDiscountBreakdownSchema = z.array(
  LocalOrderDiscountBreakdownItemSchema,
);
export type LocalOrderDiscountBreakdown = z.infer<
  typeof LocalOrderDiscountBreakdownSchema
>;

export const LocalOrderPricingSourceSchema = z.enum([
  "network",
  "cache",
  "manual",
  "mixed",
]);
export type LocalOrderPricingSource = z.infer<
  typeof LocalOrderPricingSourceSchema
>;

export const OfflineSyncCouponReplayResultSchema = z.object({
  code: z.string().min(1),
  applied: z.boolean(),
  discountAmount: z.number().optional(),
  errorCode: z.string().optional(),
  error: z.string().optional(),
});
export type OfflineSyncCouponReplayResult = z.infer<
  typeof OfflineSyncCouponReplayResultSchema
>;

export const OfflineSyncRewardReplayResultSchema = z.object({
  rewardId: z.string().min(1),
  applied: z.boolean(),
  discountAmount: z.number().optional(),
  errorCode: z.string().optional(),
  error: z.string().optional(),
});
export type OfflineSyncRewardReplayResult = z.infer<
  typeof OfflineSyncRewardReplayResultSchema
>;

export const OfflineSyncReplayResultSchema = z.object({
  serverCustomerId: z.string().optional(),
  couponReplay: z.array(OfflineSyncCouponReplayResultSchema).optional(),
  rewardReplay: OfflineSyncRewardReplayResultSchema.optional(),
});
export type OfflineSyncReplayResult = z.infer<
  typeof OfflineSyncReplayResultSchema
>;

export const CreateOrderRequestSchema = z.object({
  cartItems: z.array(CartItemSnapshotSchema).min(1),
  selection: POSSelectionSnapshotSchema,
  isDelivery: z.boolean(),
  deliveryData: DeliveryInputSnapshotSchema.optional(),
  customerId: z.string().optional(),
  couponCode: z.string().optional(),
  couponApplyToCartIndices: z.array(z.number().int().min(0)).optional(),
  couponApplications: z.array(CartCouponApplicationSchema).optional(),
});
export type CreateOrderRequest = z.infer<typeof CreateOrderRequestSchema>;

export const CheckoutAndCompleteRequestSchema = CreateOrderRequestSchema.extend(
  {
    payments: z.array(PaymentInputSnapshotSchema).min(1),
    skipKitchenValidation: z.boolean().optional(),
    voucherCodes: z.array(z.string().min(1)).optional(),
    paymentCouponCode: z.string().optional(),
    couponOrderItemIds: z.array(z.string().min(1)).optional(),
    paymentCouponCartIndices: z.array(z.number().int().min(0)).optional(),
    paymentCouponApplications: z
      .array(PaymentCouponApplicationSchema)
      .optional(),
    customerSnapshot: LocalOrderCustomerSnapshotSchema.optional(),
    couponSnapshots: z.array(LocalOrderCouponSnapshotSchema).optional(),
    voucherSnapshots: z.array(LocalOrderVoucherSnapshotSchema).optional(),
    rewardSnapshot: LocalOrderRewardSnapshotSchema.optional(),
    discountBreakdown: LocalOrderDiscountBreakdownSchema.optional(),
    pricingSource: LocalOrderPricingSourceSchema.optional(),
    rewardId: z.string().optional(),
  },
);
export type CheckoutAndCompleteRequest = z.infer<
  typeof CheckoutAndCompleteRequestSchema
>;

export const CompleteOrderRequestSchema = z.object({
  orderId: z.string().min(1),
  branchId: z.string().min(1),
  deviceId: z.string().optional(),
  payments: z.array(PaymentInputSnapshotSchema).min(1),
  skipKitchenValidation: z.boolean().optional(),
  voucherCodes: z.array(z.string().min(1)).optional(),
  couponCode: z.string().optional(),
  couponOrderItemIds: z.array(z.string().min(1)).optional(),
  couponApplications: z.array(PaymentCouponApplicationSchema).optional(),
  customerId: z.string().optional(),
  rewardId: z.string().optional(),
});
export type CompleteOrderRequest = z.infer<typeof CompleteOrderRequestSchema>;

export const OfflineOrderOperationSchema = z.enum(["CHECKOUT_AND_COMPLETE"]);
export type OfflineOrderOperation = z.infer<typeof OfflineOrderOperationSchema>;

export const OfflineOrderPayloadSchema = z.object({
  protocolVersion: z.literal(1),
  operation: OfflineOrderOperationSchema,
  clientOrderId: z.string().min(1),
  idempotencyKey: z.string().min(1),
  capturedAt: z.string().datetime(),
  appVersion: z.string().optional(),
  completedShiftId: z.string().optional(),
  request: CheckoutAndCompleteRequestSchema,
});
export type OfflineOrderPayload = z.infer<typeof OfflineOrderPayloadSchema>;

export const OfflineAdjustmentPayloadSchema = z.object({
  protocolVersion: z.literal(1),
  operation: OfflineAdjustmentOperationSchema,
  operationId: z.string().min(1),
  clientOrderId: z.string().min(1),
  idempotencyKey: z.string().min(1),
  capturedAt: z.string().datetime(),
  serverOrderId: z.string().optional(),
  performedBy: OfflineAdjustmentActorSchema.optional(),
  payload: JsonRecordSchema,
});
export type OfflineAdjustmentPayload = z.infer<
  typeof OfflineAdjustmentPayloadSchema
>;

export const SyncAdjustmentsRequestSchema = z.object({
  branchId: z.string().min(1),
  deviceId: z.string().min(1),
  shiftId: z.string().min(1),
  operations: z.array(OfflineAdjustmentPayloadSchema).min(1).max(50),
});
export type SyncAdjustmentsRequest = z.infer<
  typeof SyncAdjustmentsRequestSchema
>;

export const SyncOrdersRequestSchema = z.object({
  branchId: z.string().min(1),
  deviceId: z.string().min(1),
  shiftId: z.string().min(1),
  orders: z.array(OfflineOrderPayloadSchema).min(1).max(50),
});
export type SyncOrdersRequest = z.infer<typeof SyncOrdersRequestSchema>;

export const SyncOrderAcceptedSchema = z.object({
  clientOrderId: z.string().min(1),
  inboxId: z.string().min(1),
  status: z.enum(["accepted", "already_pending", "already_completed"]),
  serverOrderId: z.string().optional(),
});
export type SyncOrderAccepted = z.infer<typeof SyncOrderAcceptedSchema>;

export const SyncOrderRejectedSchema = z.object({
  clientOrderId: z.string().min(1),
  errorCode: z.string().min(1),
  message: z.string().min(1),
});
export type SyncOrderRejected = z.infer<typeof SyncOrderRejectedSchema>;

export const SyncOrdersResponseSchema = z.object({
  jobId: z.string().min(1),
  accepted: z.array(SyncOrderAcceptedSchema),
  rejected: z.array(SyncOrderRejectedSchema),
});
export type SyncOrdersResponse = z.infer<typeof SyncOrdersResponseSchema>;

export const SyncAdjustmentAcceptedSchema = z.object({
  operationId: z.string().min(1),
  clientOrderId: z.string().min(1),
  inboxId: z.string().min(1),
  status: z.enum(["accepted", "already_pending", "already_completed"]),
  serverOrderId: z.string().optional(),
});
export type SyncAdjustmentAccepted = z.infer<
  typeof SyncAdjustmentAcceptedSchema
>;

export const SyncAdjustmentRejectedSchema = z.object({
  operationId: z.string().min(1),
  clientOrderId: z.string().min(1),
  errorCode: z.string().min(1),
  message: z.string().min(1),
});
export type SyncAdjustmentRejected = z.infer<
  typeof SyncAdjustmentRejectedSchema
>;

export const SyncAdjustmentsResponseSchema = z.object({
  jobId: z.string().min(1),
  accepted: z.array(SyncAdjustmentAcceptedSchema),
  rejected: z.array(SyncAdjustmentRejectedSchema),
});
export type SyncAdjustmentsResponse = z.infer<
  typeof SyncAdjustmentsResponseSchema
>;
